import { Fragment, KeyboardEvent, ReactNode, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { CommonDialogHeader } from '../../components/Dialog';
import { ReturnReceptionService } from '../../api/services/returnReceptionService';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import {
  IOrderReturn,
  IReturnLookupResponse,
} from '../../types/api/response/returnreceptionresponse';
import { IReturnPackageInput } from '../../types/api/request/returnreceptionrequest';
import {
  confirmationJobChip,
  confirmationStateColor,
  formatReturnDate,
  returnStatusColor,
} from '../../components/DataGrid/ReturnReception/columns';

/**
 * What the dialog was opened with: a scan lookup, or a return fetched by ref (row click).
 */
export type ReturnDetailSource =
  | { kind: 'lookup'; lookup: IReturnLookupResponse }
  | { kind: 'return'; orderReturn: IOrderReturn };

interface Props {
  source: ReturnDetailSource | null;
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful receive/intake so the lists can refresh. */
  onCompleted: () => void;
  onResolve: (orderReturn: IOrderReturn) => void;
  /** Asks to resend the failed/stuck SuperPro confirmations (the page confirms first). */
  onResend: (orderReturn: IOrderReturn) => void;
}

type ShortOutcome = '' | 'MISSING' | 'DAMAGED';

interface PackageRow {
  barcode: string;
  pack_type: string | null;
  expected: boolean;
  received: boolean;
  outcome: ShortOutcome;
}

interface FormValues {
  packages: PackageRow[];
  remark: string;
  driver_name: string;
  driver_phone: string;
}

/** Lets a table scroll sideways on a narrow screen instead of squeezing or overflowing. */
const ScrollTable = ({ children }: { children: ReactNode }) => (
  <TableContainer sx={{ overflowX: 'auto' }}>
    <Table size="small" sx={{ minWidth: 520 }}>
      {children}
    </Table>
  </TableContainer>
);

/** Button row: right-aligned, wrapping onto a second line when the buttons don't fit. */
const ACTIONS_SX = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1.5,
  justifyContent: 'flex-end',
} as const;

const needsRemark = (packages: PackageRow[]) =>
  packages.length === 0 || packages.some((p) => !p.received);

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box display="flex" gap={1}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110, flexShrink: 0 }}>
      {label}
    </Typography>
    {/* minWidth 0 + overflowWrap so long remarks and barcode lists wrap instead of overflowing. */}
    <Typography variant="body2" component="div" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

export const ReturnDetailDialog = ({
  source,
  isOpen,
  onClose,
  onCompleted,
  onResolve,
  onResend,
}: Props) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  if (!source) return null;

  const orderReturn =
    source.kind === 'return' ? source.orderReturn : (source.lookup.order_return ?? null);
  const lookup = source.kind === 'lookup' ? source.lookup : null;
  const isIntake = lookup?.match === 'OPEN_INTAKE';
  const isReceive = !isIntake && orderReturn?.status === 'EXPECTED';
  const orderNumber = orderReturn?.order_number ?? lookup?.order.order_number ?? '';

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth fullScreen={isPhone}>
      <DialogTitle>
        <CommonDialogHeader
          title={
            isIntake
              ? `Return intake — ${orderNumber}`
              : `Return ${orderReturn?.return_ref ?? ''} — ${orderNumber}`
          }
          onClose={onClose}
        />
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {lookup?.warning && <Alert severity="warning">{lookup.warning}</Alert>}

        <Summary orderReturn={orderReturn} lookup={lookup} />

        {orderReturn?.package_mismatch && (
          <Alert severity="info">
            SuperPro&apos;s package list differs from what was packed. Confirm what actually came
            back.
            <Box mt={1}>
              <strong>Expected (SuperPro):</strong>{' '}
              {orderReturn.packages
                .filter((p) => p.expected)
                .map((p) => p.barcode)
                .join(', ') || '—'}
            </Box>
            <Box>
              <strong>Packed:</strong> {orderReturn.packed_barcodes.join(', ') || '—'}
            </Box>
          </Alert>
        )}

        <Divider />

        {isReceive || isIntake ? (
          <ReceiveForm
            key={orderReturn?.return_ref ?? `intake-${lookup?.order.order_id}`}
            orderReturn={isReceive ? orderReturn : null}
            lookup={isIntake ? lookup : null}
            onClose={onClose}
            onCompleted={onCompleted}
          />
        ) : (
          orderReturn && (
            <ReadOnlyReturn
              orderReturn={orderReturn}
              onClose={onClose}
              onResolve={onResolve}
              onResend={onResend}
            />
          )
        )}
      </DialogContent>
    </Dialog>
  );
};

const Summary = ({
  orderReturn,
  lookup,
}: {
  orderReturn: IOrderReturn | null;
  lookup: IReturnLookupResponse | null;
}) => {
  if (!orderReturn && lookup) {
    return (
      <Box display="flex" flexDirection="column" gap={0.5}>
        <DetailRow label="Order" value={lookup.order.order_number} />
        <DetailRow label="Order status" value={lookup.order.order_status} />
        <DetailRow label="Source" value={lookup.suggested_source} />
        <DetailRow label="Return status" value={<Chip label="NEW INTAKE" size="small" />} />
      </Box>
    );
  }
  if (!orderReturn) return null;
  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <DetailRow label="Order" value={orderReturn.order_number} />
      <DetailRow label="Source" value={orderReturn.source} />
      <DetailRow
        label="Status"
        value={
          <Chip
            label={orderReturn.status}
            size="small"
            color={returnStatusColor(orderReturn.status)}
          />
        }
      />
      <DetailRow
        label="Driver"
        value={
          orderReturn.driver_name || orderReturn.driver_phone
            ? [orderReturn.driver_name, orderReturn.driver_phone].filter(Boolean).join(' · ')
            : '—'
        }
      />
      <DetailRow label="Tour" value={orderReturn.tour_id ?? '—'} />
      <DetailRow label="Initiated by" value={orderReturn.initiated_by ?? '—'} />
      <DetailRow label="Reason" value={orderReturn.reason ?? '—'} />
      <DetailRow
        label="Queued"
        value={
          <>
            {formatReturnDate(orderReturn.queued_at)}
            {orderReturn.overdue && (
              <Chip label="Overdue" size="small" color="warning" sx={{ ml: 1 }} />
            )}
          </>
        }
      />
    </Box>
  );
};

/** Pretty-prints the stored JSON; falls back to the raw text if it doesn't parse. */
const formatPayload = (payload: string) => {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
};

const ConfirmationsSection = ({ orderReturn }: { orderReturn: IOrderReturn }) => {
  const [openPayloadId, setOpenPayloadId] = useState<number | null>(null);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Confirmations to SuperPro
      </Typography>
      <ScrollTable>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Event</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Attempts</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Sent</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orderReturn.confirmations.map((job) => {
            const chip = confirmationJobChip(job);
            const isPayloadOpen = openPayloadId === job.id;
            return (
              <Fragment key={job.id}>
                <TableRow>
                  <TableCell padding="checkbox">
                    {job.payload && (
                      <IconButton
                        size="small"
                        aria-label={isPayloadOpen ? 'Hide payload' : 'Show payload'}
                        onClick={() => setOpenPayloadId(isPayloadOpen ? null : job.id)}
                      >
                        {isPayloadOpen ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>
                    {job.event}
                    {job.last_error && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {job.last_error}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={chip.label} size="small" color={chip.color} />
                  </TableCell>
                  <TableCell>{job.attempts}</TableCell>
                  <TableCell>{formatReturnDate(job.created_at)}</TableCell>
                  <TableCell>{formatReturnDate(job.sent_at)}</TableCell>
                </TableRow>
                {job.payload && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      sx={{ py: 0, borderBottom: isPayloadOpen ? undefined : 'none' }}
                    >
                      <Collapse in={isPayloadOpen} unmountOnExit>
                        <Typography variant="caption" color="text.secondary">
                          Payload sent to SuperPro
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            mb: 1,
                            p: 1,
                            fontSize: 12,
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            overflowX: 'auto',
                          }}
                        >
                          {formatPayload(job.payload)}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </ScrollTable>
    </Box>
  );
};

const ReadOnlyReturn = ({
  orderReturn,
  onClose,
  onResolve,
  onResend,
}: {
  orderReturn: IOrderReturn;
  onClose: () => void;
  onResolve: (orderReturn: IOrderReturn) => void;
  onResend: (orderReturn: IOrderReturn) => void;
}) => (
  <Box display="flex" flexDirection="column" gap={2}>
    <Box display="flex" flexDirection="column" gap={0.5}>
      <DetailRow
        label="Received"
        value={
          orderReturn.received_at
            ? `${formatReturnDate(orderReturn.received_at)} by ${orderReturn.received_by ?? '—'}`
            : '—'
        }
      />
      <DetailRow label="Remark" value={orderReturn.remark ?? '—'} />
      {(orderReturn.resolved_at || orderReturn.resolution_remark) && (
        <>
          <DetailRow
            label="Resolved"
            value={`${formatReturnDate(orderReturn.resolved_at)} by ${orderReturn.resolved_by ?? '—'}`}
          />
          <DetailRow label="Resolution" value={orderReturn.resolution_remark ?? '—'} />
        </>
      )}
      {orderReturn.confirmation_state && (
        <DetailRow
          label="Confirmation"
          value={
            <Chip
              label={orderReturn.confirmation_state}
              size="small"
              color={confirmationStateColor(orderReturn.confirmation_state)}
            />
          }
        />
      )}
    </Box>

    <ScrollTable>
      <TableHead>
        <TableRow>
          <TableCell>Barcode</TableCell>
          <TableCell>Pack type</TableCell>
          <TableCell>Expected</TableCell>
          <TableCell>Outcome</TableCell>
          <TableCell>Entry</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {orderReturn.packages.length === 0 && (
          <TableRow>
            <TableCell colSpan={5}>
              <Typography variant="body2" color="text.secondary">
                No packages recorded.
              </Typography>
            </TableCell>
          </TableRow>
        )}
        {orderReturn.packages.map((p) => (
          <TableRow key={p.barcode}>
            <TableCell>{p.barcode}</TableCell>
            <TableCell>{p.pack_type ?? '—'}</TableCell>
            <TableCell>{p.expected ? 'Yes' : 'No'}</TableCell>
            <TableCell>
              {p.outcome ? (
                <Chip
                  label={p.outcome}
                  size="small"
                  color={p.outcome === 'RECEIVED' ? 'success' : 'error'}
                />
              ) : (
                '—'
              )}
            </TableCell>
            <TableCell>{p.entry_method ?? '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </ScrollTable>

    {orderReturn.confirmations?.length > 0 && <ConfirmationsSection orderReturn={orderReturn} />}

    <Box sx={ACTIONS_SX}>
      <Button variant="outlined" onClick={onClose}>
        Close
      </Button>
      {/* FAILED covers stuck jobs too — the only state the API will resend. */}
      {orderReturn.confirmation_state === 'FAILED' && (
        <Button variant="contained" onClick={() => onResend(orderReturn)}>
          Resend confirmation
        </Button>
      )}
      {orderReturn.status === 'SHORT' && (
        <Button variant="contained" color="warning" onClick={() => onResolve(orderReturn)}>
          Resolve
        </Button>
      )}
    </Box>
  </Box>
);

const ReceiveForm = ({
  orderReturn,
  lookup,
  onClose,
  onCompleted,
}: {
  orderReturn: IOrderReturn | null;
  lookup: IReturnLookupResponse | null;
  onClose: () => void;
  onCompleted: () => void;
}) => {
  const dispatch = useDispatch();
  const isIntake = !orderReturn && !!lookup;

  const initialPackages: PackageRow[] = orderReturn
    ? orderReturn.packages
        .filter((p) => p.expected)
        .map((p) => ({
          barcode: p.barcode,
          pack_type: p.pack_type,
          expected: true,
          received: true,
          outcome: '',
        }))
    : (lookup?.order.packages ?? []).map((p) => ({
        barcode: p.barcode,
        pack_type: p.pack_type,
        expected: true,
        received: true,
        outcome: '',
      }));

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { packages: initialPackages, remark: '', driver_name: '', driver_phone: '' },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'packages' });

  const [newBarcode, setNewBarcode] = useState('');
  const [newBarcodeError, setNewBarcodeError] = useState<string | null>(null);

  const packages = watch('packages');
  const remarkRequired = needsRemark(packages);

  const addParcel = () => {
    const barcode = newBarcode.trim();
    if (!barcode) return;
    if (getValues('packages').some((p) => p.barcode === barcode)) {
      setNewBarcodeError('That barcode is already listed.');
      return;
    }
    append({ barcode, pack_type: null, expected: false, received: true, outcome: '' });
    setNewBarcode('');
    setNewBarcodeError(null);
  };

  const onAddKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Keyboard-wedge scanners finish with Enter — add the parcel instead of submitting the form.
    if (e.key === 'Enter') {
      e.preventDefault();
      addParcel();
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    const payloadPackages: IReturnPackageInput[] = values.packages.map((p) => ({
      barcode: p.barcode,
      outcome: p.received ? 'RECEIVED' : (p.outcome as 'MISSING' | 'DAMAGED'),
      entry_method: p.expected ? 'CONFIRMED' : 'TYPED',
      pack_type: p.pack_type ?? null,
    }));
    const remark = values.remark.trim() || undefined;

    try {
      const result = orderReturn
        ? await ReturnReceptionService.receive(orderReturn.return_ref, {
            remark,
            packages: payloadPackages,
          })
        : await ReturnReceptionService.intake({
            order_id: lookup!.order.order_id,
            driver_name: values.driver_name.trim() || undefined,
            driver_phone: values.driver_phone.trim() || undefined,
            remark,
            packages: payloadPackages,
          });
      dispatch(
        showNotification({
          message:
            result.status === 'SHORT'
              ? `Return ${result.return_ref} recorded as SHORT.`
              : `Return ${result.return_ref} received.`,
          severity: result.status === 'SHORT' ? 'warning' : 'success',
        }),
      );
      onClose();
      onCompleted();
    } catch (error: any) {
      const status = error?.response?.status;
      dispatch(
        showNotification({
          message:
            status === 403
              ? 'You do not have permission to receive returns.'
              : (error?.response?.data?.message ??
                (status === 409
                  ? 'This return has already been processed.'
                  : 'Failed to record the return.')),
          severity: 'error',
        }),
      );
    }
  });

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      noValidate
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Typography variant="subtitle2">Packages</Typography>

      <ScrollTable>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">Received</TableCell>
            <TableCell>Barcode</TableCell>
            <TableCell>Pack type</TableCell>
            <TableCell>If not received</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">
                  No packages on record for this order. Add the parcels below, or confirm with a
                  remark.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {fields.map((field, index) => {
            const row = packages[index];
            return (
              <TableRow key={field.id}>
                <TableCell padding="checkbox">
                  {field.expected ? (
                    <Controller
                      name={`packages.${index}.received`}
                      control={control}
                      render={({ field: f }) => (
                        <Checkbox
                          checked={!!f.value}
                          onChange={(e) => {
                            f.onChange(e.target.checked);
                            if (e.target.checked) {
                              setValue(`packages.${index}.outcome`, '', { shouldValidate: true });
                            }
                          }}
                        />
                      )}
                    />
                  ) : (
                    <Checkbox checked disabled />
                  )}
                </TableCell>
                <TableCell>
                  {field.barcode}
                  {!field.expected && (
                    <Chip label="Added" size="small" variant="outlined" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>{field.pack_type ?? '—'}</TableCell>
                <TableCell>
                  {row && !row.received && (
                    <Controller
                      name={`packages.${index}.outcome`}
                      control={control}
                      rules={{
                        validate: (value) =>
                          getValues(`packages.${index}.received`) || value
                            ? true
                            : 'Choose Missing or Damaged',
                      }}
                      render={({ field: f }) => (
                        <FormControl
                          size="small"
                          error={!!errors.packages?.[index]?.outcome}
                          sx={{ minWidth: 140 }}
                        >
                          <Select value={f.value} onChange={f.onChange} displayEmpty>
                            <MenuItem value="" disabled>
                              Select…
                            </MenuItem>
                            <MenuItem value="MISSING">Missing</MenuItem>
                            <MenuItem value="DAMAGED">Damaged</MenuItem>
                          </Select>
                          {errors.packages?.[index]?.outcome && (
                            <FormHelperText>
                              {errors.packages[index]?.outcome?.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  )}
                </TableCell>
                <TableCell padding="checkbox">
                  {!field.expected && (
                    <IconButton size="small" onClick={() => remove(index)} aria-label="Remove">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </ScrollTable>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          alignItems: { xs: 'stretch', sm: 'flex-start' },
        }}
      >
        <TextField
          size="small"
          label="Add parcel (type or scan barcode)"
          value={newBarcode}
          onChange={(e) => {
            setNewBarcode(e.target.value);
            setNewBarcodeError(null);
          }}
          onKeyDown={onAddKeyDown}
          error={!!newBarcodeError}
          helperText={newBarcodeError ?? 'A parcel not in the expected list, e.g. re-packaged.'}
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          onClick={addParcel}
          disabled={!newBarcode.trim()}
          sx={{ flexShrink: 0, height: { sm: 40 } }}
        >
          Add parcel
        </Button>
      </Box>

      {isIntake && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
          <TextField
            size="small"
            label="Brought back by (optional)"
            fullWidth
            {...register('driver_name')}
          />
          <TextField
            size="small"
            label="Phone (optional)"
            fullWidth
            {...register('driver_phone')}
          />
        </Box>
      )}

      <TextField
        label={remarkRequired ? 'Remark (required)' : 'Remark (optional)'}
        multiline
        rows={2}
        fullWidth
        error={!!errors.remark}
        helperText={
          errors.remark?.message ??
          (remarkRequired
            ? packages.length === 0
              ? 'Required when there are no packages.'
              : 'Required when a package is missing or damaged.'
            : undefined)
        }
        {...register('remark', {
          validate: (value, values) =>
            needsRemark(values.packages) && !value.trim()
              ? 'A remark is required for a short return or one with no packages.'
              : true,
        })}
      />

      <Box sx={ACTIONS_SX}>
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color={remarkRequired && packages.length > 0 ? 'warning' : 'primary'}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? 'Saving...'
            : packages.some((p) => !p.received)
              ? 'Confirm (short)'
              : 'Confirm all received'}
        </Button>
      </Box>
    </Box>
  );
};
